import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatIST } from '@/lib/utils';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { Trash2, RotateCcw, AlertTriangle, Package, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton } from '@/components/data';
import PhoneRecycleBin from './Admin/PhoneRecycleBin';
import { useIsMobile } from '@/hooks/useIsMobile';
import { toast } from 'sonner';

export default function RecycleBinPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Deleted orders state
  const isMobile = useIsMobile();
  const [deletedOrders, setDeletedOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersLoading, setOrdersLoading] = useState(true);
  
  // Deleted line items state
  const [deletedLineItems, setDeletedLineItems] = useState([]);
  const [lineItemsTotal, setLineItemsTotal] = useState(0);
  const [lineItemsPage, setLineItemsPage] = useState(1);
  const [lineItemsLoading, setLineItemsLoading] = useState(true);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', item: null });
  const [actionLoading, setActionLoading] = useState(false);

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Access denied. Admin only.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch deleted orders
  const fetchDeletedOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await api.get('/orders/recycle-bin/orders', { params: { page: ordersPage, limit: 20 } });
      setDeletedOrders(res.data.orders || []);
      setOrdersTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load deleted orders');
    }
    setOrdersLoading(false);
  }, [ordersPage]);

  // Fetch deleted line items
  const fetchDeletedLineItems = useCallback(async () => {
    setLineItemsLoading(true);
    try {
      const res = await api.get('/orders/recycle-bin/line-items', { params: { page: lineItemsPage, limit: 20 } });
      setDeletedLineItems(res.data.line_items || []);
      setLineItemsTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load deleted line items');
    }
    setLineItemsLoading(false);
  }, [lineItemsPage]);

  // both lists are on screen together now, so both load together
  useEffect(() => {
    fetchDeletedOrders();
    fetchDeletedLineItems();
  }, [fetchDeletedOrders, fetchDeletedLineItems]);

  // Restore order
  const handleRestoreOrder = async (order) => {
    setActionLoading(true);
    try {
      await api.post(`/orders/recycle-bin/orders/${order.id}/restore`);
      toast.success(`Order ${order.order_id} restored successfully`);
      fetchDeletedOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore order');
    }
    setActionLoading(false);
    setConfirmDialog({ open: false, type: '', item: null });
  };

  // Restore line item
  const handleRestoreLineItem = async (item) => {
    setActionLoading(true);
    try {
      await api.post(`/orders/recycle-bin/line-items/${item.line_item_id}/restore`);
      toast.success(`Line item "${item.item_name}" restored successfully`);
      fetchDeletedLineItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore line item');
    }
    setActionLoading(false);
    setConfirmDialog({ open: false, type: '', item: null });
  };

  // Permanent delete order
  const handlePermanentDeleteOrder = async (order) => {
    setActionLoading(true);
    try {
      await api.delete(`/orders/recycle-bin/orders/${order.id}/permanent`);
      toast.success(`Order ${order.order_id} permanently deleted`);
      fetchDeletedOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to permanently delete order');
    }
    setActionLoading(false);
    setConfirmDialog({ open: false, type: '', item: null });
  };

  // Permanent delete line item
  const handlePermanentDeleteLineItem = async (item) => {
    setActionLoading(true);
    try {
      await api.delete(`/orders/recycle-bin/line-items/${item.line_item_id}/permanent`);
      toast.success(`Line item "${item.item_name}" permanently deleted`);
      fetchDeletedLineItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to permanently delete line item');
    }
    setActionLoading(false);
    setConfirmDialog({ open: false, type: '', item: null });
  };

  const openConfirmDialog = (type, item) => {
    setConfirmDialog({ open: true, type, item });
  };

  const handleConfirmAction = () => {
    const { type, item } = confirmDialog;
    if (type === 'restore-order') handleRestoreOrder(item);
    else if (type === 'restore-line-item') handleRestoreLineItem(item);
    else if (type === 'permanent-delete-order') handlePermanentDeleteOrder(item);
    else if (type === 'permanent-delete-line-item') handlePermanentDeleteLineItem(item);
  };

  const ordersPageCount = Math.ceil(ordersTotal / 20);
  const lineItemsPageCount = Math.ceil(lineItemsTotal / 20);

  if (!user || user.role !== 'admin') return null;

  // Orders and line items are the same question at two scales, so they are one
  // list with a Type column rather than two tabs you have to remember to check.
  const rows = [
    ...deletedOrders.map(o => ({ ...o, __kind: 'order', __rowId: `o:${o.id}` })),
    ...deletedLineItems.map(i => ({ ...i, __kind: 'line-item', __rowId: `l:${i.id}` })),
  ];

  const columns = [
    {
      key: 'type',
      header: 'Type',
      width: '6.5rem',
      render: (r) => (
        <span className={`text-[11px] font-medium ${r.__kind === 'order' ? 'text-brand-blue' : 'text-slate-500'}`}>
          {r.__kind === 'order' ? 'Order' : 'Line item'}
        </span>
      ),
    },
    {
      key: 'what',
      header: 'What was deleted',
      width: 'minmax(14rem,1.6fr)',
      render: (r) => (
        <>
          <span className="block truncate font-semibold text-slate-900">
            {r.__kind === 'order'
              ? (r.line_items?.[0]?.item_name || r.customer_name || r.order_id)
              : (r.item_name || r.product_subtype || r.order_type || 'Item')}
          </span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">
            {r.order_id || r.order_display_id}
            {r.customer_name ? ` · ${r.customer_name}` : ''}
          </span>
        </>
      ),
    },
    {
      key: 'size',
      header: 'Size',
      align: 'right',
      width: '7rem',
      render: (r) => r.__kind === 'order'
        ? <span className="text-[12px] text-slate-600">{r.line_items?.length || 0} items</span>
        : <span className="text-[12px] text-slate-600">Qty {r.quantity?.toLocaleString() || '—'}</span>,
    },
    {
      key: 'deleted_by_name',
      header: 'Deleted by',
      width: '9rem',
      render: (r) => <span className="block truncate text-[12px] text-slate-600">{r.deleted_by_name || '—'}</span>,
    },
    {
      key: 'deleted_at',
      header: 'When',
      width: '10rem',
      render: (r) => (
        <span className="whitespace-nowrap text-[11px] text-slate-500">
          {r.deleted_at ? formatIST(r.deleted_at, { compact: true }) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '10rem',
      align: 'right',
      render: (r) => (
        <span className="flex items-center justify-end gap-0.5">
          <CellButton
            icon={RotateCcw}
            label="Restore"
            tone="good"
            onClick={() => setConfirmDialog({
              open: true,
              type: r.__kind === 'order' ? 'restore-order' : 'restore-line-item',
              item: r,
            })}
          />
          <CellButton
            icon={Trash2}
            label="Delete forever"
            tone="danger"
            onClick={() => setConfirmDialog({
              open: true,
              type: r.__kind === 'order' ? 'permanent-delete-order' : 'permanent-delete-line-item',
              item: r,
            })}
          />
        </span>
      ),
    },
  ];

  const isDestructive = confirmDialog.type?.startsWith('permanent-delete');

  const panels = (
    <>
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog(c => ({ ...c, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {isDestructive && <AlertTriangle className="h-5 w-5 text-red-500" />}
              {isDestructive ? 'Delete permanently?' : 'Restore this?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isDestructive
                ? 'This cannot be undone. The record and its history are removed for good.'
                : 'It goes back where it was, with its job cards and history intact.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              disabled={actionLoading}
              className={isDestructive ? 'bg-red-600 text-white hover:bg-red-700' : ''}
            >
              {actionLoading ? 'Working…' : isDestructive ? 'Delete forever' : 'Restore'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  if (isMobile) {
    return (
      <>
        <PhoneRecycleBin
          rows={rows}
          loading={ordersLoading && lineItemsLoading}
          ordersTotal={ordersTotal}
          lineItemsTotal={lineItemsTotal}
          ordersPage={ordersPage}
          ordersPageCount={ordersPageCount}
          onOrdersPage={setOrdersPage}
          lineItemsPage={lineItemsPage}
          lineItemsPageCount={lineItemsPageCount}
          onLineItemsPage={setLineItemsPage}
          onRefresh={() => { fetchDeletedOrders(); fetchDeletedLineItems(); }}
          onRestore={(r) => openConfirmDialog(r.__kind === 'order' ? 'restore-order' : 'restore-line-item', r)}
          onDeleteForever={(r) => openConfirmDialog(r.__kind === 'order' ? 'permanent-delete-order' : 'permanent-delete-line-item', r)}
        />
        {panels}
      </>
    );
  }

  return (
    <Screen data-testid="recycle-bin-page">
      <CommandBar
        title="Recycle Bin"
        subtitle="Deleted orders and line items, restorable until they are purged"
        actions={[
          {
            key: 'refresh', label: 'Refresh', icon: RefreshCw,
            onClick: () => { fetchDeletedOrders(); fetchDeletedLineItems(); },
          },
        ]}
      />

      <StatStrip
        stats={[
          { key: null, label: 'deleted orders', value: ordersTotal, tone: 'blue' },
          { key: null, label: 'deleted line items', value: lineItemsTotal },
        ]}
      >
        {(ordersPageCount > 1 || lineItemsPageCount > 1) && (
          <span className="flex items-center gap-2 text-[11px] text-slate-500">
            {ordersPageCount > 1 && (
              <span className="flex items-center gap-1">
                Orders
                <button type="button" disabled={ordersPage <= 1} onClick={() => setOrdersPage(p => p - 1)}
                  className="rounded px-1 hover:bg-slate-100 disabled:opacity-30">
                  <ChevronLeft className="h-3 w-3" />
                </button>
                {ordersPage}/{ordersPageCount}
                <button type="button" disabled={ordersPage >= ordersPageCount} onClick={() => setOrdersPage(p => p + 1)}
                  className="rounded px-1 hover:bg-slate-100 disabled:opacity-30">
                  <ChevronRight className="h-3 w-3" />
                </button>
              </span>
            )}
            {lineItemsPageCount > 1 && (
              <span className="flex items-center gap-1">
                Items
                <button type="button" disabled={lineItemsPage <= 1} onClick={() => setLineItemsPage(p => p - 1)}
                  className="rounded px-1 hover:bg-slate-100 disabled:opacity-30">
                  <ChevronLeft className="h-3 w-3" />
                </button>
                {lineItemsPage}/{lineItemsPageCount}
                <button type="button" disabled={lineItemsPage >= lineItemsPageCount} onClick={() => setLineItemsPage(p => p + 1)}
                  className="rounded px-1 hover:bg-slate-100 disabled:opacity-30">
                  <ChevronRight className="h-3 w-3" />
                </button>
              </span>
            )}
          </span>
        )}
      </StatStrip>

      <Pane title="Deleted" aside={`${rows.length} recoverable`} scroll={false} bodyClassName="flex flex-col">
        <DataGrid
          data-testid="recycle-bin-table"
          columns={columns}
          rows={rows}
          loading={ordersLoading && lineItemsLoading}
          getRowId={(r) => r.__rowId}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <Package className="h-8 w-8 text-slate-300" />
              Nothing deleted — the bin is empty.
            </span>
          }
        />
      </Pane>

      {panels}
    </Screen>
  );
}
